import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { OCCUPATION_OPTIONS, isOccupation, type Occupation } from "@/lib/occupation";

export const Route = createFileRoute("/_authenticated/complete-profile")({
  head: () => ({
    meta: [{ title: "Complete your profile — VISIKT" }, { name: "robots", content: "noindex" }],
  }),
  component: CompleteProfilePage,
});

import { COUNTRIES } from "@/lib/countries";
const phoneRegex = /^[+\d][\d\s\-()]{6,}$/;

function CompleteProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const profileQ = useProfile();
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    postal_code: "",
    city: "",
    country: "Norway",
    phone: "",
    occupation: "" as Occupation | "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const p = profileQ.data as Record<string, string | null> | null | undefined;
    if (!p) return;
    setForm((f) => ({
      first_name: f.first_name || p.first_name || "",
      last_name: f.last_name || p.last_name || "",
      postal_code: f.postal_code || p.postal_code || "",
      city: f.city || p.city || "",
      country: f.country || p.country || "Norway",
      phone: f.phone || p.phone || "",
      occupation: f.occupation || (isOccupation(p.occupation) ? p.occupation : ""),
    }));
  }, [profileQ.data]);

  function validate() {
    const next: Record<string, string> = {};
    if (!form.first_name.trim()) next.first_name = t("register.errors.required");
    if (!form.last_name.trim()) next.last_name = t("register.errors.required");
    if (!form.city.trim()) next.city = t("register.errors.required");
    if (!form.country.trim()) next.country = t("register.errors.required");
    if (form.country === "Norway") {
      if (!/^\d{4}$/.test(form.postal_code.trim()))
        next.postal_code = t("register.errors.postalNo");
    } else if (!form.postal_code.trim()) {
      next.postal_code = t("register.errors.required");
    }
    if (!phoneRegex.test(form.phone.trim())) next.phone = t("register.errors.phone");
    if (!isOccupation(form.occupation)) next.occupation = t("register.errors.required");
    if (!agree) next.agree = t("completeProfile.agreeRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) {
      navigate({ to: "/auth", replace: true });
      return;
    }
    const { error } = await supabase
      .from("profiles")
      .update({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        postal_code: form.postal_code.trim(),
        city: form.city.trim(),
        country: form.country,
        phone: form.phone.trim(),
        occupation: form.occupation,
        display_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
      })
      .eq("id", u.user.id);
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    await qc.invalidateQueries({ queryKey: ["profile"] });
    toast.success(t("completeProfile.saved"));
    navigate({ to: "/plans", replace: true });
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="max-w-md mx-auto px-5 pt-8 pb-6">
        <Link to="/" className="font-display font-extrabold tracking-tighter text-xl">
          VISIKT
        </Link>
      </header>
      <main className="max-w-md mx-auto px-5 pb-16">
        <div className="mb-8 animate-entry">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            {t("completeProfile.eyebrow")}
          </p>
          <h1 className="text-3xl font-display font-bold tracking-tight">
            {t("completeProfile.headline")}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">{t("completeProfile.subtitle")}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("register.firstName")} error={errors.first_name}>
              <input
                type="text"
                required
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                maxLength={80}
                autoComplete="given-name"
                className="input-base"
              />
            </Field>
            <Field label={t("register.lastName")} error={errors.last_name}>
              <input
                type="text"
                required
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                maxLength={80}
                autoComplete="family-name"
                className="input-base"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("register.postalCode")} error={errors.postal_code}>
              <input
                type="text"
                required
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                maxLength={16}
                className="input-base"
              />
            </Field>
            <Field label={t("register.city")} error={errors.city}>
              <input
                type="text"
                required
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                maxLength={80}
                className="input-base"
              />
            </Field>
          </div>
          <Field label={t("register.country")} error={errors.country}>
            <select
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="input-base"
            >
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("register.phone")} error={errors.phone}>
            <input
              type="tel"
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              maxLength={32}
              className="input-base"
              placeholder="+47 900 00 000"
            />
          </Field>
          <Field label={t("occupation.label")} error={errors.occupation}>
            <select
              required
              value={form.occupation}
              onChange={(e) => setForm({ ...form, occupation: e.target.value as Occupation | "" })}
              className="input-base"
            >
              <option value="" disabled>
                {t("occupation.placeholder")}
              </option>
              {OCCUPATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </option>
              ))}
            </select>
          </Field>
          <label className="flex items-start gap-2.5 text-xs text-muted-foreground mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 size-4 accent-accent flex-shrink-0"
              aria-invalid={!!errors.agree}
            />
            <span className="leading-relaxed">
              <Trans
                i18nKey="completeProfile.agreeTos"
                components={{
                  tosLink: (
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-4 text-foreground"
                    />
                  ),
                  privLink: (
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-4 text-foreground"
                    />
                  ),
                }}
              />
            </span>
          </label>
          {errors.agree && (
            <span className="block text-[11px] text-destructive">{errors.agree}</span>
          )}
          <button
            type="submit"
            disabled={loading || !agree}
            className="w-full py-3 rounded-md bg-accent text-primary font-medium text-sm disabled:opacity-50 mt-2"
          >
            {loading ? t("auth.waiting") : t("completeProfile.submit")}
          </button>
        </form>
      </main>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}
      </span>
      {children}
      {error && <span className="block text-[11px] text-destructive mt-1">{error}</span>}
    </label>
  );
}

// Ensure z is used (validation stays local for future extension)
void z;
